import React, { useState, useCallback, useMemo, useEffect, useRef, Fragment } from 'react';
import VideoTrackCard from './VideoTrackCard';
import { VideoOverlayTrack } from './types';
import {
  addToRecentlyUsed,
  getRecentlyUsed,
  isCustomUpload,
  createCustomTrackFromUrl
} from './videoOverlayLibraryUtils';
import { useVideoOverlaySearch } from './useVideoOverlaySearch';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import Button from '@/components/atoms/Button/Button';
import IconButton from '@/components/atoms/Button/IconButton';
import FilesInput from '@/components/atoms/inputs/FilesInput/FilesInput';
import { showErrorToast } from '@/libs/toast/toast';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import { VIDEO_FORMATS } from '@/components/molecules/MultiAssetUploader/constants';
import Icon from '@/components/atoms/Icon';

type SelectionSource = 'library' | 'upload' | null;

interface VideoOverlayLibraryModalProps {
  onClose: () => void;
  onConfirm: (
    selection: { type: 'library'; video: VideoOverlayTrack } | { type: 'upload'; file: File }
  ) => Promise<string | undefined>;
  currentVideo?: VideoOverlayTrack | null;
}

const VideoOverlayLibraryModal: React.FC<VideoOverlayLibraryModalProps> = ({
  onClose,
  onConfirm,
  currentVideo = null
}) => {
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [recentlyUsedTracks, setRecentlyUsedTracks] = useState<VideoOverlayTrack[]>([]);

  const { clipData } = useClipsContext();
  const transcriptStore = useTranscriptContext();
  const chapters = useMemo(() => transcriptStore.chapterData, [transcriptStore.chapterData]);

  const initialQuery = useMemo(() => {
    if (currentVideo?.name) {
      return currentVideo.name;
    }

    const firstChapter = chapters && chapters.length > 0 ? chapters[0] : null;
    if (firstChapter?.title) {
      return firstChapter.title;
    }

    if (clipData.content?.project?.title) {
      return clipData.content.project.title;
    }

    return '';
  }, [currentVideo?.name, chapters, clipData.content?.project?.title]);

  const [searchQuery, setSearchQuery] = useState<string>(initialQuery);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  const { search, results, loading, error, hasMore, loadMore, reset, totalResults, searchedQuery } =
    useVideoOverlaySearch();

  const trimmedQuery = searchQuery.trim();
  const isPending = trimmedQuery !== '' && trimmedQuery !== searchedQuery && !loading;
  const showSkeleton = (loading || isPending) && results.length === 0 && !error;
  const showError = !!error && !loading;
  const showEmpty = !trimmedQuery && results.length === 0 && !loading && !error && recentlyUsedTracks.length === 0;
  const showNoResults =
    trimmedQuery !== '' && trimmedQuery === searchedQuery && results.length === 0 && !loading && !error;
  const showResults = results.length > 0 && !error;
  const showLoadingMore = loading && results.length > 0 && hasMore && trimmedQuery === searchedQuery && !error;

  const isCurrentVideoCustomUpload = useMemo(() => {
    return currentVideo !== null && isCustomUpload(currentVideo);
  }, [currentVideo]);

  const lastSelectionSource: SelectionSource = useMemo(() => {
    if (uploadedFile) {
      return 'upload';
    }
    if (selectedVideoId) {
      const selectedTrack =
        results.find(v => v.id === selectedVideoId) || recentlyUsedTracks.find(v => v.id === selectedVideoId);
      if (selectedTrack && isCustomUpload(selectedTrack)) {
        return 'upload';
      }
    }
    if (selectedVideoId) {
      return 'library';
    }
    return null;
  }, [uploadedFile, selectedVideoId, results, recentlyUsedTracks]);

  useEffect(() => {
    setRecentlyUsedTracks(getRecentlyUsed());

    if (currentVideo) {
      setSelectedVideoId(currentVideo.id);
      setUploadedFile(null);
    } else {
      setSelectedVideoId(null);
      setUploadedFile(null);
    }
    setIsSubmitting(false);

    setSearchQuery(initialQuery);

    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);

    return () => {
      setSelectedVideoId(null);
      setUploadedFile(null);
      setIsSubmitting(false);
    };
  }, [currentVideo, initialQuery]);

  useEffect(() => {
    if (searchQuery.trim()) {
      search(searchQuery.trim());
    } else {
      reset();
    }
  }, [searchQuery, search, reset]);

  useEffect(() => {
    const container = resultsContainerRef.current;
    if (!container || !hasMore || loading) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop <= clientHeight + 100) {
        loadMore();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [hasMore, loading, loadMore]);

  const handleVideoSelect = useCallback(
    (videoId: string) => {
      if (isSubmitting) {
        return;
      }
      setSelectedVideoId(videoId);
      setUploadedFile(null);
      setErrorMessage(null);
    },
    [isSubmitting]
  );

  const handleUploadConfirm = useCallback(
    async (file: File) => {
      const uploadedUrl = await onConfirm({ type: 'upload', file });
      if (uploadedUrl) {
        const customTrack = createCustomTrackFromUrl(file, uploadedUrl);
        addToRecentlyUsed(customTrack);
        setRecentlyUsedTracks(getRecentlyUsed());
      }
    },
    [onConfirm]
  );

  const handleFileUpload = useCallback(
    async (files: File[]) => {
      if (files.length > 0 && !isSubmitting) {
        const file = files[0];
        setUploadedFile(file);
        setSelectedVideoId(null);
        setErrorMessage(null);
        setIsSubmitting(true);
        try {
          await handleUploadConfirm(file);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to upload video';
          setErrorMessage(message);
          showErrorToast(message);
          setIsSubmitting(false);
        }
      }
    },
    [handleUploadConfirm, isSubmitting]
  );

  const handleCancel = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    onClose();
  }, [onClose, isSubmitting]);

  const handleRetrySearch = useCallback(() => {
    if (trimmedQuery) {
      reset();
      setTimeout(() => search(trimmedQuery), 50);
    }
  }, [trimmedQuery, reset, search]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  }, []);

  const handleLibraryConfirm = useCallback(
    async (videoTrack: VideoOverlayTrack) => {
      await onConfirm({ type: 'library', video: videoTrack });
      addToRecentlyUsed(videoTrack);
      setRecentlyUsedTracks(getRecentlyUsed());
    },
    [onConfirm]
  );

  const handleConfirm = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    const isReselectingSameCustomUpload =
      lastSelectionSource === 'upload' &&
      !uploadedFile &&
      selectedVideoId === currentVideo?.id &&
      isCurrentVideoCustomUpload;

    if (isReselectingSameCustomUpload) {
      onClose();
      return;
    }

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      if (uploadedFile) {
        await handleUploadConfirm(uploadedFile);
      } else if (selectedVideoId) {
        const videoTrack =
          results.find(v => v.id === selectedVideoId) || recentlyUsedTracks.find(v => v.id === selectedVideoId);

        if (!videoTrack) {
          const message = 'Video not found';
          setErrorMessage(message);
          showErrorToast(message);
          setIsSubmitting(false);
          return;
        }

        await handleLibraryConfirm(videoTrack);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to confirm selection';
      setErrorMessage(message);
      showErrorToast(message);
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    lastSelectionSource,
    uploadedFile,
    selectedVideoId,
    currentVideo?.id,
    isCurrentVideoCustomUpload,
    onClose,
    handleUploadConfirm,
    handleLibraryConfirm,
    results,
    recentlyUsedTracks
  ]);

  const acceptedFormats = VIDEO_FORMATS.map(format => `.${format}`).join(',');

  const isConfirmEnabled = useMemo(() => {
    if (lastSelectionSource === null) {
      return false;
    }

    if (
      lastSelectionSource === 'upload' &&
      currentVideo &&
      selectedVideoId === currentVideo.id &&
      isCurrentVideoCustomUpload
    ) {
      return true;
    }

    return selectedVideoId !== null || uploadedFile !== null;
  }, [lastSelectionSource, selectedVideoId, uploadedFile, currentVideo, isCurrentVideoCustomUpload]);

  const modalTitle = currentVideo ? 'Replace Video' : 'Add Video';

  return (
    <ContentDialog
      isOpen={true}
      setIsOpen={onClose}
      title={modalTitle}
      size="large"
      panelClassNames="!overflow-hidden flex flex-col h-[75vh]"
    >
      <div
        className={`flex min-h-0 flex-1 flex-col ${isSubmitting ? 'pointer-events-none' : ''}`}
        data-testid="video-overlay-library-modal"
        onClick={e => e.stopPropagation()}
        onPointerDown={e => e.stopPropagation()}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div data-testid="upload-section" className="shrink-0 border-b border-slate-200 px-4 pb-6 pt-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Upload Custom Video</h3>
            <div className="relative h-[120px]">
              <FilesInput
                onFilesUpload={handleFileUpload}
                accept={acceptedFormats}
                multiple={false}
                label={uploadedFile ? uploadedFile.name : 'Click to browse, or drag & drop a video file'}
                variant="default"
                isDisabled={isSubmitting}
              />
            </div>
          </div>

          <div role="search" className="shrink-0 border-b border-slate-200 p-4">
            <div className="relative">
              <Icon
                name="IconSearch"
                className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                ref={searchInputRef}
                type="text"
                data-testid="search-input"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search royalty-free stock videos..."
                aria-label="Search royalty-free stock videos"
                aria-describedby="search-results-count"
                className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-theme-border focus:outline-none focus:ring-1 focus:ring-theme-border"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-testid="scrollable-results-container">
            <div
              ref={resultsContainerRef}
              className={`flex flex-col px-4 py-6 ${showError || showEmpty || showNoResults ? 'min-h-full' : ''}`}
              data-testid="results-container"
            >
              {!trimmedQuery && recentlyUsedTracks.length > 0 && (
                <div data-testid="recently-used-section" className="mb-6">
                  <h3 className="mb-3 text-sm font-semibold text-gray-900">Recently Used</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {recentlyUsedTracks.map(track => {
                      const isCustom = isCustomUpload(track);
                      const isSelected = isCustom
                        ? lastSelectionSource === 'upload' && selectedVideoId === track.id
                        : lastSelectionSource === 'library' && selectedVideoId === track.id;
                      return (
                        <VideoTrackCard
                          key={track.id}
                          track={track}
                          isSelected={isSelected}
                          onClick={handleVideoSelect}
                          disabled={isSubmitting}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              <div
                data-testid="search-results-section"
                className={`flex flex-col ${showError || showEmpty || showNoResults ? 'min-h-full' : ''}`}
              >
                <div className="mb-3 flex items-center justify-between">
                  {showResults && totalResults > 0 && (
                    <>
                      <h3 className="text-sm font-semibold text-gray-900">Royalty-free Stock Videos</h3>
                      <span data-testid="results-count" className="text-xs text-gray-500">
                        Showing {results.length} of {totalResults}
                      </span>
                    </>
                  )}
                </div>
                <div id="search-results-count" className="sr-only" aria-live="polite" aria-atomic="true">
                  {loading
                    ? 'Loading videos...'
                    : results.length > 0
                      ? `${results.length} of ${totalResults} videos found`
                      : trimmedQuery
                        ? 'No videos found'
                        : ''}
                </div>

                {showSkeleton && (
                  <div className="grid grid-cols-3 gap-3" data-testid="skeleton-grid">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Fragment key={i}>
                        <div className="h-[10.75rem] animate-pulse rounded-lg bg-gray-200" aria-hidden="true" />
                      </Fragment>
                    ))}
                  </div>
                )}

                {showError && (
                  <div
                    data-testid="error-state"
                    className="flex min-h-full flex-1 flex-col items-center justify-center py-8 text-center"
                  >
                    <Icon name="IconAlertCircle" className="mb-2 size-8 text-red-400" aria-hidden="true" />
                    <p data-testid="error-message" className="mb-3 text-sm text-red-600">
                      {error?.message || 'Failed to load videos'}
                    </p>
                    <button
                      data-testid="retry-search-button"
                      type="button"
                      onClick={handleRetrySearch}
                      className="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                      <Icon name="IconRefresh" className="size-4" aria-hidden="true" />
                      Retry
                    </button>
                  </div>
                )}

                {showEmpty && (
                  <div
                    data-testid="empty-state"
                    className="flex min-h-full flex-1 flex-col items-center justify-center py-8 text-center"
                  >
                    <Icon name="IconSearch" className="mb-2 size-8 text-gray-300" aria-hidden="true" />
                    <p data-testid="empty-state-message" className="text-sm text-gray-500">
                      Search for royalty-free stock videos to get started.
                    </p>
                  </div>
                )}

                {showNoResults && (
                  <div
                    data-testid="no-results-state"
                    className="flex min-h-full flex-1 flex-col items-center justify-center py-8 text-center"
                  >
                    <Icon name="IconSearch" className="mb-2 size-8 text-gray-300" aria-hidden="true" />
                    <p data-testid="no-results-message" className="mb-3 text-sm text-gray-500">
                      No videos found for "{searchQuery}".
                    </p>
                    <button
                      data-testid="clear-search-button"
                      type="button"
                      onClick={handleClearSearch}
                      className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                      <Icon name="IconX" className="size-4" aria-hidden="true" />
                      Clear search
                    </button>
                  </div>
                )}

                {showResults && (
                  <div data-testid="results-grid" className="grid grid-cols-3 gap-3">
                    {results.map(track => (
                      <VideoTrackCard
                        key={track.id}
                        track={track}
                        isSelected={lastSelectionSource === 'library' && selectedVideoId === track.id}
                        onClick={handleVideoSelect}
                        disabled={isSubmitting}
                      />
                    ))}
                  </div>
                )}

                {showLoadingMore && (
                  <div data-testid="loading-more-indicator" className="mt-4 flex items-center justify-center">
                    <Icon name="IconLoader2" className="size-5 animate-spin text-gray-400" aria-hidden="true" />
                    <span data-testid="loading-more-text" className="ml-2 text-sm text-gray-500">
                      Loading more...
                    </span>
                  </div>
                )}
              </div>
            </div>

            {errorMessage && (
              <div data-testid="error-message-container" className="border-t border-slate-200 px-4 py-3" role="alert">
                <p data-testid="error-message-text" className="text-sm text-red-600">
                  {errorMessage}
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 justify-end gap-3 border-t border-slate-200 px-4 py-2">
          <Button
            data-testid="cancel-button"
            variation="outline"
            trackingId="cancel-button"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <IconButton
            data-testid="confirm-button"
            icon={isSubmitting ? 'IconLoader2' : 'IconCheck'}
            iconClassName={isSubmitting ? 'animate-spin' : ''}
            content={isSubmitting ? 'Submitting...' : 'Confirm'}
            variation="filled"
            trackingId="confirm-button"
            onClick={handleConfirm}
            disabled={!isConfirmEnabled || isSubmitting}
          />
        </div>
      </div>
    </ContentDialog>
  );
};

export default VideoOverlayLibraryModal;
